import _ from 'lodash';
import moment from 'moment-timezone';
import createUserManager from 'helpers/createUserManager';
import { getQueryStringValue } from 'helpers/queryString';
import { computed, observable, reaction, runInAction } from 'mobx';
import { get, post, UNAUTHORIZED_EVENT } from 'services/request';
import WebStorage from 'services/WebStorage';
import { prepareUserConfig } from '../../../helpers/prepareConfig';
import app from './AppStore';

export const readwriteRoles = ['admin', 'owner', 'agent', 'assistant'];
export const readonlyRoles = ['readonly-agent'].concat(readwriteRoles);

class UserStore {
  @observable data = null;
  @observable error = null;
  @observable claims = null;

  @observable favicon_url = null;
  @observable friendly_name = null;

  app = null;

  storage = new WebStorage();

  roleDefinitions = {
    readonly: readonlyRoles,
  };

  @computed get userConfig() {
    return prepareUserConfig(this.app.config, this.app.data, this.claims);
  }

  constructor(app) {
    this.app = app;
    this.appInit = reaction(
      () => app.config,
      (config) => {
        this.createUserManager(config);
      }
    );
    this.tokenReaction = reaction(
      () => this.accessToken,
      async (token) => {
        if (!this.accessToken) {
          return;
        }
        if (this.claims) {
          return;
        }
        this.claims = (await get('/api/v1/users', this.accessToken)).claims;
      }
    );
    this.profileReaction = reaction(
      () => this.profile,
      (claims) => {
        if (claims !== null) {
          const locale = claims.locale;
          window.locale = locale;
          moment.locale(locale);
        }
      }
    );
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener(UNAUTHORIZED_EVENT, this.onUnauthorized);
    }
  }

  @computed get hasRole() {
    const { permissions } = this.profile;
    return (roles) => {
      if (!permissions) {
        return true;
      }
      return permissions.some((p) => {
        return roles.some((r) => r === p.role);
      });
    };
  }

  @computed get hasReadWritePermissions() {
    return this.hasRole(readwriteRoles);
  }

  @computed get hasPermissions() {
    const office =
      typeof this.app.features.officeRequired === 'undefined'
        ? true
        : !!this.app.features.officeRequired;
    return (
      entityAgentData,
      roles = readwriteRoles,
      officeRequired = office
    ) => {
      const agentTenant = this.profile.tenant || this.app.tenant;
      const entityTenant = entityAgentData.tenant || this.app.tenant;

      if (
        !!this.profile.sub &&
        !!entityAgentData.sub &&
        this.profile.sub === entityAgentData.sub
      ) {
        return true;
      }

      if (
        agentTenant === entityTenant &&
        this.profile.permissions &&
        this.profile.permissions.length > 0
      ) {
        const permissions = this.profile.permissions;
        return permissions
          .filter(
            (p) =>
              !officeRequired ||
              !!p.office ||
              p.role === 'admin' ||
              p.role === 'owner' ||
              p.role === 'assistant'
          )
          .some((p) => {
            return (
              (!p.office || p.office === entityAgentData.office_id) && // has office permissions
              (!p.agent || p.agent === entityAgentData.id) &&
              roles.some((r) => r === p.role)
            );
          });
      }

      if (
        entityTenant === agentTenant &&
        this.profile.office_ids_allowed &&
        this.profile.office_ids_allowed
          .split(';')
          .includes(entityAgentData.office_id) &&
        (this.profile.hasOfficeRight || this.profile.hasBrokerownerRight)
      ) {
        return true;
      }

      return false;
    };
  }

  @computed get isAuthenticated() {
    return this.data !== null && this.claims !== null;
  }

  @computed get profile() {
    if (!this.isAuthenticated) {
      return null;
    }
    return this.claims;
  }

  @computed get accessToken() {
    return this.data !== null ? this.data.access_token : null;
  }

  @computed get sub() {
    return getQueryStringValue('sub');
  }

  get isUsingCustomIdentityService() {
    return !!app.config.oidc.user_endpoint;
  }

  async signin() {
    const usertoken = getQueryStringValue('usertoken');
    let idp = getQueryStringValue('idp');
    // if (!idp || idp === '') {
    //   idp = this.app.tenant === 'remax-at' ? 'privatenet' : null;
    // }
    const idpAcrValue = idp ? `idp:${idp}` : null;
    if (usertoken || idp) {
      await this.manager.clearStaleState();
      await this.manager.removeUser();
    }
    if (usertoken) {
      await this.manager.signinRedirect({
        extraQueryParams: { usertoken },
      });
      return;
    }

    const user = await this.manager.getUser();
    if (user && !user.expired) {
      this.onUserLoaded(user);
    } else {
      await this.manager.signinRedirect({
        acr_values: idpAcrValue,
      });
    }
  }

  async signout() {
    const doPost = this.isUsingCustomIdentityService;
    try {
      if (doPost) {
        await post('/api/v1/users/signout', {}, this.accessToken);
        //await get(`${this.manager.settings.authority}/signout`);
      }
    } catch (error) {
    } finally {
      runInAction(async () => {
        this.data = null;
        this.claims = null;
        this.error = null;
        await this.manager.removeUser();
      });
      return { redirect: !doPost };
    }
  }

  // An API call was rejected with 401: the identity session is no longer valid
  // (e.g. it was invalidated server-side). Drop the stale oidc user and start a
  // fresh sign-in instead of leaving the UI to retry with a dead token.
  onUnauthorized = async () => {
    if (!this.manager || this.reauthenticating) {
      return;
    }
    this.reauthenticating = true;
    try {
      console.debug('[auth] unauthorized response, re-authenticating');
      await this.manager.removeUser();
      await this.signin();
    } catch (e) {
      console.error('[auth] re-authentication failed', e);
    } finally {
      this.reauthenticating = false;
    }
  };

  createUserManager(config) {
    if (!config) {
      return;
    }
    this.manager = createUserManager(config.oidc);
    this.manager.events.addUserLoaded(this.onUserLoaded);
    this.manager.events.addSilentRenewError(this.onSilentRenewError);
    this.manager.events.addAccessTokenExpired(this.onAccessTokenExpired);
    this.manager.events.addAccessTokenExpiring(this.onAccessTokenExpiring);
    this.manager.events.addUserUnloaded(this.onUserUnloaded);
    this.manager.events.addUserSignedOut(this.onUserSignedOut);
  }

  onUserLoaded = async (user) => {
    if (
      this.app.tenant.startsWith('propnow') ||
      this.app.tenant === 'remax-de'
    ) {
      const appPalette = this.app.theme.palette;
      const defaultTheme = {
        palette: {
          primary: { main: appPalette.themePrimary },
          secondary: {
            main: appPalette.themeSecondary || appPalette.themeDarkAlt,
          },
          error: { main: appPalette.alert },
        },
      };
      try {
        const {
          theme = defaultTheme,
          logo_url,
          favicon_url,
          friendly_name,
          sale_country,
          features,
          dashboard,
          agent_terms_url,
          agent_privacy_url,
          agent_avv_url,
        } = await get(
          `${this.app.config.userManagement.url}/app-settings?name=${
            user.profile.tenant || this.app.tenant
          }`
        );

        const fabric = _.merge({}, this.app.theme, {
          palette: {
            themePrimary: _.get(theme, 'palette.primary.main'),
            themeSecondary: _.get(theme, 'palette.secondary.main'),
            themeDarkAlt: _.get(theme, 'palette.primary.main'),
            alert: _.get(theme, 'palette.error.main'),
          },
          logo_url,
          favicon_url,
          friendly_name,
        });

        runInAction(() => {
          this.app.setData({
            friendly_name,
            sale_country: user.profile.sale_country || sale_country,
            features,
            dashboard,
            agent_terms_url,
            agent_privacy_url,
            agent_avv_url,
          });
          this.app.setTheme(fabric);
        });
      } catch (e) {
        console.error(e);
      }
    }

    if (user.profile) {
      this.claims = user.profile;
    }
    this.data = user;

    console.debug('[oidc] onUserLoaded');
  };

  // event callback when silent renew errored
  onSilentRenewError = (error) => {
    this.error = error;
  };

  // event callback when the user is expiring
  onAccessTokenExpiring = () => {
    console.debug('[oidc] onAccessTokenExpiring');
  };

  // event callback when the access token expired
  onAccessTokenExpired = () => {
    console.debug('[oidc] onAccessTokenExpired');
  };

  // event callback when the user is signed out
  onUserSignedOut = () => {
    // this.data = null;
    console.debug('[oidc] onUserSignedOut');
  };

  // event callback when the user is logged out
  onUserUnloaded = () => {
    this.data = null;
    console.debug('[oidc] onUserUnloaded');
    if (this.isUsingCustomIdentityService) {
      this.signin();
    }
  };

  offices = () => [
    ...new Set([
      ...(!!this.claims.permissions &&
      Array.isArray(this.claims.permissions.toJS())
        ? this.claims.permissions
            .toJS()
            .filter(({ role, office, agent }) => {
              return role === 'agent' && !!agent && !!office;
            })
            .map((a) => a.office)
        : []),
      ...(!!this.claims.office_ids_allowed
        ? this.claims.office_ids_allowed.split(';')
        : []),
    ]),
  ];

  office_id = () => this.offices().find(Boolean); // get first office

  agentData = () => {
    const { name, family_name, given_name } = this.claims;
    const userName = family_name ? `${given_name} ${family_name}` : name;
    const {
      office_ids_allowed,
      permissions,
      hasBrokerownerRight,
      hasOfficeRight,
      s_hash,
      sid,
      auth_time,
      idp,
      amr,
      ...rest
    } = this.claims; // separate claims that should not go into agent_data
    return {
      ...rest,

      id: this.claims.agent_id,
      name: userName,
      phone: this.claims.phone_number,
      office_id: this.office_id(),
    };
  };
}

const user = new UserStore(app);

export default user;
export { UserStore };



// WEBPACK FOOTER //
// ./src/app/containers/App/stores/UserStore.js